import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";

export interface VoiceOption {
  name: string;
  lang: string;
  isDefault: boolean;
  voiceURI: string;
}

export interface PrometheusVoiceConfig {
  pitch: number;
  rate: number;
  volume: number;
  languageMode: "es-latam" | "en-us" | "bilingual";
  provider: "browser" | "openai-hd";
  openaiVoice: "alloy" | "echo" | "fable" | "onyx" | "nova" | "shimmer";
  selectedVoiceURI?: string;
  autoSpeakResponses: boolean;
  soundEffects: boolean;
}

const DEFAULT_CONFIG: PrometheusVoiceConfig = {
  pitch: 0.90,
  rate: 1.05,
  volume: 1.0,
  languageMode: "es-latam",
  provider: "browser",
  openaiVoice: "onyx",
  autoSpeakResponses: true,
  soundEffects: true,
};

// Technical Phonetic Pronunciation Dictionary for Prometheus
export const phoneticSanitizer = (text: string, lang: "es" | "en" | "bilingual" = "es"): string => {
  let cleaned = text
    .replace(/```[\s\S]*?```/g, lang === "en" ? "Engineering code block omitted for voice." : "Bloque de código de ingeniería omitido para audio.")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/[\*\_#]/g, "")
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1")
    .replace(/(\r\n|\n|\r)/gm, " ")
    .trim();

  if (lang === "es" || lang === "bilingual") {
    // Phonetic replacements for Spanish & Bilingual Pronunciation (Prometeo + Fluid Engineering Units)
    const replacements: [RegExp, string][] = [
      [/\bPrometheus\s+Lab\b/gi, "Laboratorio Prometeo"],
      [/\bPrometheus\b/gi, "Prometeo"],
      [/(\d+)\s*kWh\b/gi, "$1 kilovatios hora"],
      [/(\d+)\s*kW\b/gi, "$1 kilovatios"],
      [/(\d+)\s*W\b/gi, "$1 vatios"],
      [/(\d+)\s*V\b/gi, "$1 voltios"],
      [/(\d+)\s*Ah\b/gi, "$1 amperios hora"],
      [/(\d+)\s*A\b/gi, "$1 amperios"],
      [/(\d+)\s*Hz\b/gi, "$1 hercios"],
      [/(\d+)\s*kHz\b/gi, "$1 kilohercios"],
      [/(\d+)\s*MHz\b/gi, "$1 megahercios"],
      [/(\d+)\s*GHz\b/gi, "$1 gigahercios"],
      [/\bESP32\b/gi, "E-S-P treinta y dos"],
      [/\bESP32-S3\b/gi, "E-S-P treinta y dos S tres"],
      [/\bRP2040\b/gi, "R-P veinte cuarenta"],
      [/\bNEMA\s*17\b/gi, "Nema diecisiete"],
      [/\bNEMA\s*23\b/gi, "Nema veintitrés"],
      [/\bBOM\b/gi, "B-O-M, lista de materiales,"],
      [/\bPCB\b/gi, "tarjeta P-C-B"],
      [/\bPCBs\b/gi, "tarjetas P-C-B"],
      [/\bG-Code\b/gi, "código G"],
      [/\bI2S\b/gi, "I dos S"],
      [/\bI2C\b/gi, "I dos C"],
      [/\bSPI\b/gi, "S-P-I"],
      [/\bUART\b/gi, "U-ART"],
      [/\bLoRa\b/gi, "Lóra"],
      [/\bLoRaWAN\b/gi, "Lórawan"],
      [/\bDeep\s*Sleep\b/gi, "reposo profundo"],
      [/\bWebRTC\b/gi, "Web-R-T-C"],
      [/\bWebSocket\b/gi, "Web-Socket"],
      [/\bWebSerial\b/gi, "Web-Serial"],
      [/\bmAh\b/g, "miliamperios hora"],
      [/\bµA\b/g, "microamperios"],
      [/\buA\b/g, "microamperios"],
      [/\bmA\b/g, "miliamperios"],
      [/\bkΩ\b/gi, "kilo-ohmios"],
      [/\bMΩ\b/gi, "mega-ohmios"],
      [/\bΩ\b/g, "ohmios"],
      [/\bVCC\b/gi, "V-C-C positivo"],
      [/\bGND\b/gi, "tierra"],
      [/\bADC\b/gi, "A-D-C"],
      [/\bPWM\b/gi, "P-W-M"],
      [/\bWS2812B\b/gi, "LEDs NeoPixel"],
      [/\bRGB\b/gi, "R-G-B"],
      [/\bLiFePO4\b/gi, "litio ferrofosfato"],
      [/\bLi-Ion\b/gi, "litio ion"],
      [/\bDoD\b/gi, "profundidad de descarga"],
      [/\bSoC\b/gi, "estado de carga"],
      [/\b3D\b/gi, "tres D"],
      [/\bCNC\b/gi, "C-N-C"],
      [/\bCAD\b/gi, "C-A-D"],
      [/\bCAM\b/gi, "C-A-M"],
      [/\bHP\b/g, "caballos de fuerza"],
      [/\bRPM\b/gi, "revoluciones por minuto"],
      [/\bASCE\b/gi, "A-S-C-E"],
      [/\bPR\b/g, "Puerto Rico"],
    ];

    replacements.forEach(([regex, repl]) => {
      cleaned = cleaned.replace(regex, repl);
    });

    // Remove markdown list markers and replace colons with natural commas for continuous cadence
    cleaned = cleaned
      .replace(/^\s*[\-\*\•]\s+/gm, "")
      .replace(/:\s+/g, ", ")
      .replace(/\s{2,}/g, " ")
      .trim();
  }

  return cleaned;
};

export const usePrometheusVoice = () => {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [spanishVoices, setSpanishVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [englishVoices, setEnglishVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [speechSupported, setSpeechSupported] = useState(true);
  const [recognitionSupported, setRecognitionSupported] = useState(true);

  const [config, setConfig] = useState<PrometheusVoiceConfig>(() => {
    try {
      const saved = localStorage.getItem("prometheus_voice_config");
      return saved ? { ...DEFAULT_CONFIG, ...JSON.parse(saved) } : DEFAULT_CONFIG;
    } catch {
      return DEFAULT_CONFIG;
    }
  });

  const recognitionRef = useRef<any>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Initialize Web Audio Context for Chimes & Feedback
  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current && typeof window !== "undefined") {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        audioCtxRef.current = new AudioCtx();
      }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  // Play synthetic feedback chime (no external audio files required)
  const playChime = useCallback((type: "start" | "recognized" | "stop") => {
    if (!config.soundEffects) return;
    try {
      const ctx = getAudioContext();
      if (!ctx) return;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      const now = ctx.currentTime;
      if (type === "start") {
        // High-tech ascending chirp
        osc.type = "sine";
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.exponentialRampToValueAtTime(880, now + 0.12);
        gain.gain.setValueAtTime(0.08, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
        osc.start(now);
        osc.stop(now + 0.15);
      } else if (type === "recognized") {
        // Double success beep (A5 -> C#6)
        osc.type = "triangle";
        osc.frequency.setValueAtTime(587.33, now); // D5
        osc.frequency.setValueAtTime(880, now + 0.08); // A5
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
      } else if (type === "stop") {
        // Soft descending tone
        osc.type = "sine";
        osc.frequency.setValueAtTime(660, now);
        osc.frequency.exponentialRampToValueAtTime(330, now + 0.12);
        gain.gain.setValueAtTime(0.06, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
        osc.start(now);
        osc.stop(now + 0.15);
      }
    } catch {}
  }, [config.soundEffects, getAudioContext]);

  // Save config to local storage
  const updateConfig = useCallback((newConfig: Partial<PrometheusVoiceConfig>) => {
    setConfig((prev) => {
      const updated = { ...prev, ...newConfig };
      // If languageMode changed, clear selectedVoiceURI so loadVoices picks the best voice for the new language
      if (newConfig.languageMode && newConfig.languageMode !== prev.languageMode) {
        updated.selectedVoiceURI = "";
      }
      // If voice timbre changed, map to distinct acoustic tone profile
      if (newConfig.openaiVoice && newConfig.openaiVoice !== prev.openaiVoice) {
        const timbreProfiles: Record<string, { pitch: number; rate: number }> = {
          onyx: { pitch: 0.90, rate: 1.05 },
          echo: { pitch: 0.96, rate: 1.04 },
          alloy: { pitch: 1.0, rate: 1.04 },
          fable: { pitch: 1.06, rate: 1.05 },
          nova: { pitch: 1.10, rate: 1.05 },
          shimmer: { pitch: 1.18, rate: 1.06 },
        };
        const p = timbreProfiles[newConfig.openaiVoice];
        if (p) {
          updated.pitch = p.pitch;
          updated.rate = p.rate;
        }
      }
      try {
        localStorage.setItem("prometheus_voice_config", JSON.stringify(updated));
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("prometheus_voice_config_updated", { detail: updated }));
        }
      } catch {}
      return updated;
    });
  }, []);

  // Load and score voices (Latin Spanish, English US, Neural Natural)
  const loadVoices = useCallback(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setSpeechSupported(false);
      return;
    }

    const available = window.speechSynthesis.getVoices();
    setVoices(available);

    const esVoices = available.filter((v) => 
      v.lang.toLowerCase().startsWith("es") || 
      v.lang.toLowerCase().includes("spa")
    );
    setSpanishVoices(esVoices);

    const enVoices = available.filter((v) => 
      v.lang.toLowerCase().startsWith("en") || 
      v.lang.toLowerCase().includes("eng")
    );
    setEnglishVoices(enVoices);

    // Scoring system prioritizes ultra-natural / neural Latin and US voices
    const scoreVoice = (v: SpeechSynthesisVoice, langMode: PrometheusVoiceConfig["languageMode"]) => {
      const name = v.name.toLowerCase();
      const uri = v.voiceURI.toLowerCase();
      const lang = v.lang.toLowerCase();
      let s = 0;

      // Neural / Natural Quality
      if (name.includes("natural") || uri.includes("natural")) s += 120;
      if (name.includes("online") || uri.includes("online")) s += 90;
      if (name.includes("neural") || uri.includes("neural")) s += 110;
      if (name.includes("google")) s += 75;
      if (name.includes("premium") || name.includes("enhanced")) s += 50;

      if (langMode === "es-latam") {
        if (lang.includes("mx") || lang.includes("us") || lang.includes("419") || lang.includes("co") || lang.includes("pr") || lang.includes("cl") || lang.includes("ar")) s += 60;
        if (name.includes("jorge") || name.includes("gonzalo") || name.includes("alvaro") || name.includes("dalia") || name.includes("paulino") || name.includes("sabina") || name.includes("elena") || name.includes("raquel") || name.includes("luis")) s += 40;
        if (!lang.startsWith("es")) s -= 200;
      } else if (langMode === "en-us") {
        if (lang.includes("us")) s += 60;
        if (name.includes("guy") || name.includes("christopher") || name.includes("eric") || name.includes("roger") || name.includes("steffan") || name.includes("david") || name.includes("mark")) s += 40;
        if (!lang.startsWith("en")) s -= 200;
      } else {
        // Bilingual: prioritize voices that handle multilingual or clear neutral accent
        if (lang.startsWith("es") || lang.startsWith("en")) s += 30;
        if (name.includes("natural")) s += 50;
      }

      return s;
    };

    const targetList = config.languageMode === "en-us" ? enVoices : esVoices;
    const sorted = [...(targetList.length > 0 ? targetList : available)].sort(
      (a, b) => scoreVoice(b, config.languageMode) - scoreVoice(a, config.languageMode)
    );

    // If a specific voice was selected, verify it matches current languageMode
    if (config.selectedVoiceURI) {
      const match = available.find((v) => v.voiceURI === config.selectedVoiceURI);
      if (match) {
        const isLangMatch = config.languageMode === "en-us"
          ? match.lang.toLowerCase().startsWith("en")
          : config.languageMode === "es-latam"
          ? match.lang.toLowerCase().startsWith("es")
          : true;
        if (isLangMatch) {
          setSelectedVoice(match);
          return;
        }
      }
    }

    if (sorted.length > 0) {
      setSelectedVoice(sorted[0]);
    } else if (available.length > 0) {
      setSelectedVoice(available[0]);
    }
  }, [config.selectedVoiceURI, config.languageMode]);

  useEffect(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    } else {
      setSpeechSupported(false);
    }
  }, [loadVoices]);

  // Real-time synchronization of voice config across all components (Settings, Workshop, Chat)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleSync = (e: Event) => {
      const customEvent = e as CustomEvent<PrometheusVoiceConfig>;
      const newCfg = customEvent.detail;
      if (newCfg) {
        setConfig((prev) => ({ ...prev, ...newCfg }));
        if (newCfg.selectedVoiceURI && typeof window !== "undefined" && window.speechSynthesis) {
          const allVoices = window.speechSynthesis.getVoices();
          const match = allVoices.find((v) => v.voiceURI === newCfg.selectedVoiceURI);
          if (match) setSelectedVoice(match);
        }
      }
    };

    window.addEventListener("prometheus_voice_config_updated", handleSync);
    window.addEventListener("storage", handleSync);
    return () => {
      window.removeEventListener("prometheus_voice_config_updated", handleSync);
      window.removeEventListener("storage", handleSync);
    };
  }, []);

  // Check Web Speech Recognition
  useEffect(() => {
    const SpeechRec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRec) {
      setRecognitionSupported(false);
    }
  }, []);

  const activeAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const activeAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const activeAbortControllerRef = useRef<AbortController | null>(null);
  const currentlyPlayingTextRef = useRef<string | null>(null);
  const [speakingText, setSpeakingText] = useState<string | null>(null);

  // Stop any active speech (both Remote/OpenAI Audio and Browser SpeechSynthesis)
  const stopSpeaking = useCallback(() => {
    // 1. Abort any in-flight HTTP requests
    if (activeAbortControllerRef.current) {
      try {
        activeAbortControllerRef.current.abort();
      } catch {}
      activeAbortControllerRef.current = null;
    }

    // 2. Stop and clear any active HTMLAudioElement immediately
    if (activeAudioElementRef.current) {
      try {
        activeAudioElementRef.current.pause();
        activeAudioElementRef.current.currentTime = 0;
        activeAudioElementRef.current.src = "";
      } catch {}
      activeAudioElementRef.current = null;
    }

    // 3. Stop WebAudio buffer source if active
    if (activeAudioSourceRef.current) {
      try {
        activeAudioSourceRef.current.stop();
        activeAudioSourceRef.current.disconnect();
      } catch {}
      activeAudioSourceRef.current = null;
    }

    // 4. Cancel native browser SpeechSynthesis immediately
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    }

    currentlyPlayingTextRef.current = null;
    setSpeakingText(null);
    setIsSpeaking(false);
  }, []);

  // Text-To-Speech execution (Dual: OpenAI HD Human Voice or Instant Browser Natural Engine)
  const speak = useCallback(async (text: string, onEnd?: () => void) => {
    if (!text || text.trim() === "") return;

    // Toggle behavior: If already speaking this exact text, clicking again stops immediately
    if (currentlyPlayingTextRef.current === text) {
      stopSpeaking();
      return;
    }

    // Stop any ongoing speech or pending fetch before starting anew
    stopSpeaking();

    // Mark current text and speaking state immediately for reactive UI feedback
    currentlyPlayingTextRef.current = text;
    setSpeakingText(text);
    setIsSpeaking(true);

    // Apply phonetic sanitizer with intelligent technical expansions
    const mode = config.languageMode === "en-us" ? "en" : config.languageMode === "bilingual" ? "bilingual" : "es";
    const cleanText = phoneticSanitizer(text, mode);

    const openaiKey = typeof window !== "undefined" ? (localStorage.getItem("PROMETHEUS_OPENAI_API_KEY") || "") : "";

    // 1. Try Neural HD TTS only if explicitly configured for openai-hd AND an API key is available
    if (config.provider === "openai-hd" && openaiKey) {
      const abortController = new AbortController();
      activeAbortControllerRef.current = abortController;

      try {
        const response = await fetch("https://api.openai.com/v1/audio/speech", {
          method: "POST",
          signal: abortController.signal,
          headers: {
            "Authorization": `Bearer ${openaiKey}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            model: "tts-1-hd",
            voice: config.openaiVoice || "onyx",
            input: cleanText,
            speed: Math.max(0.85, Math.min(1.25, config.rate))
          })
        });

        if (response.ok) {
          const blob = await response.blob();
          if (abortController.signal.aborted || currentlyPlayingTextRef.current !== text) {
            return;
          }
          const audioUrl = URL.createObjectURL(blob);
          const audio = new Audio(audioUrl);
          activeAudioElementRef.current = audio;
          audio.volume = config.volume;
          audio.playbackRate = Math.max(0.85, Math.min(1.25, config.rate));
          audio.onended = () => {
            if (activeAudioElementRef.current === audio) {
              activeAudioElementRef.current = null;
            }
            if (currentlyPlayingTextRef.current === text) {
              currentlyPlayingTextRef.current = null;
              setSpeakingText(null);
              setIsSpeaking(false);
            }
            URL.revokeObjectURL(audioUrl);
            if (onEnd) onEnd();
          };
          audio.onerror = () => {
            if (activeAudioElementRef.current === audio) {
              activeAudioElementRef.current = null;
            }
            if (currentlyPlayingTextRef.current === text) {
              currentlyPlayingTextRef.current = null;
              setSpeakingText(null);
              setIsSpeaking(false);
            }
            URL.revokeObjectURL(audioUrl);
          };
          await audio.play();
          return;
        }
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        console.warn("OpenAI Direct TTS failed, falling back to Browser Voice:", err);
      }
    }

    // 2. High-Performance Instant Browser Natural Voice Synthesis (starts in < 50ms)
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      currentlyPlayingTextRef.current = null;
      setSpeakingText(null);
      setIsSpeaking(false);
      return;
    }

    // Cancel again right before speaking to ensure pristine browser synthesis queue
    window.speechSynthesis.cancel();

    // Group text into coherent clauses to preserve natural cadence and eliminate robotic pauses
    const sentences = cleanText.length < 320
      ? [cleanText]
      : (cleanText.match(/[^.!?]+[.!?]+|[^.!?]+$/g)?.map(s => s.trim()).filter(Boolean) || [cleanText]);

    if (sentences.length === 0) {
      currentlyPlayingTextRef.current = null;
      setSpeakingText(null);
      setIsSpeaking(false);
      return;
    }

    let currentIdx = 0;

    const speakNextSentence = () => {
      // Check if cancelled or text changed
      if (currentlyPlayingTextRef.current !== text) {
        return;
      }

      if (currentIdx >= sentences.length) {
        currentlyPlayingTextRef.current = null;
        setSpeakingText(null);
        setIsSpeaking(false);
        if (onEnd) onEnd();
        return;
      }

      const sentenceText = sentences[currentIdx];
      const utterance = new SpeechSynthesisUtterance(sentenceText);

      // Guarantee target voice matches the active language mode
      let targetVoice = selectedVoice;
      const isEnglishMode = config.languageMode === "en-us";
      if (targetVoice) {
        const voiceIsEnglish = targetVoice.lang.toLowerCase().startsWith("en");
        if (isEnglishMode !== voiceIsEnglish) {
          const list = isEnglishMode ? englishVoices : spanishVoices;
          if (list && list.length > 0) {
            targetVoice = list[0];
          }
        }
      }

      if (targetVoice) {
        utterance.voice = targetVoice;
        utterance.lang = targetVoice.lang;
      } else {
        utterance.lang = isEnglishMode ? "en-US" : "es-MX";
      }

      utterance.pitch = config.pitch;
      utterance.rate = config.rate;
      utterance.volume = config.volume;

      utterance.onend = () => {
        currentIdx++;
        speakNextSentence();
      };

      utterance.onerror = (e: any) => {
        if (e.error === "interrupted" || e.error === "canceled") {
          if (currentlyPlayingTextRef.current === text) {
            currentlyPlayingTextRef.current = null;
            setSpeakingText(null);
            setIsSpeaking(false);
          }
          return;
        }
        console.warn("TTS sentence error:", e.error || e);
        currentIdx++;
        speakNextSentence();
      };

      window.speechSynthesis.speak(utterance);
    };

    speakNextSentence();
  }, [selectedVoice, config, englishVoices, spanishVoices, stopSpeaking]);

  // Speech-To-Text Listener
  const startListening = useCallback((
    onResult?: (text: string) => void,
    options?: { continuous?: boolean; lang?: string }
  ) => {
    const SpeechRec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRec) {
      toast.error("Tu navegador no soporta reconocimiento de voz (SpeechRecognition)");
      return;
    }

    try {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }

      const rec = new SpeechRec();
      rec.continuous = options?.continuous ?? true;
      rec.interimResults = true;
      rec.lang = options?.lang || "es-ES";

      rec.onstart = () => {
        setIsListening(true);
        playChime("start");
      };

      rec.onresult = (event: any) => {
        let currentTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          currentTranscript += event.results[i][0].transcript;
        }
        setTranscript(currentTranscript);

        // If final result
        const lastResult = event.results[event.results.length - 1];
        if (lastResult.isFinal && onResult) {
          playChime("recognized");
          onResult(lastResult[0].transcript);
        }
      };

      rec.onerror = (event: any) => {
        if (event.error !== "no-speech") {
          console.warn("Speech Recognition Error:", event.error);
        }
      };

      rec.onend = () => {
        setIsListening(false);
        playChime("stop");
      };

      rec.start();
      recognitionRef.current = rec;
    } catch (err) {
      console.warn("Error starting speech recognition:", err);
      setIsListening(false);
    }
  }, [playChime]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const selectVoiceByURI = useCallback((voiceURI: string) => {
    const match = voices.find((v) => v.voiceURI === voiceURI);
    if (match) {
      setSelectedVoice(match);
      updateConfig({ selectedVoiceURI: voiceURI });
      stopSpeaking();
      const isEnglish = match.lang.toLowerCase().startsWith("en");
      const previewText = isEnglish
        ? "Prometheus acoustic engine online. Voice profile successfully calibrated."
        : "Voz de Prometheus configurada correctamente en el taller.";
      setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(previewText);
        utterance.voice = match;
        utterance.lang = match.lang;
        utterance.pitch = config.pitch;
        utterance.rate = config.rate;
        window.speechSynthesis.speak(utterance);
      }, 70);
    }
  }, [voices, config, updateConfig, stopSpeaking]);

  return {
    isSpeaking,
    speakingText,
    isListening,
    transcript,
    speechSupported,
    recognitionSupported,
    voices,
    spanishVoices,
    englishVoices,
    selectedVoice,
    config,
    updateConfig,
    selectVoiceByURI,
    speak,
    stopSpeaking,
    startListening,
    stopListening,
    playChime,
  };
};
